/**
 * Keywords Cache Utility
 * Manages caching and retrieval of technology keywords for autocomplete
 */

interface KeywordsCacheData {
  keywords: string[];
  timestamp: number;
  totalCount: number;
}

class KeywordsCache {
  private static instance: KeywordsCache;
  private cacheKey = 'utech_keywords_cache';
  private cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  private keywords: string[] = [];
  private isLoading = false;
  private loadPromise: Promise<string[]> | null = null;

  static getInstance(): KeywordsCache {
    if (!KeywordsCache.instance) {
      KeywordsCache.instance = new KeywordsCache();
    }
    return KeywordsCache.instance;
  }

  /**
   * Get keywords from cache or fetch from API
   */
  async getKeywords(): Promise<string[]> {
    // If already loading, return the existing promise
    if (this.isLoading && this.loadPromise) {
      return this.loadPromise;
    }

    // If keywords are already loaded and fresh, return them
    if (this.keywords.length > 0 && this.isCacheFresh()) {
      return this.keywords;
    }

    // Start loading
    this.isLoading = true;
    this.loadPromise = this.loadKeywordsFromCacheOrAPI();

    try {
      const keywords = await this.loadPromise;
      this.keywords = keywords;
      return keywords;
    } finally {
      this.isLoading = false;
      this.loadPromise = null;
    }
  }

  /**
   * Search keywords that match the input query
   */
  async searchKeywords(query: string, limit: number = 20): Promise<string[]> {
    const allKeywords = await this.getKeywords();
    
    if (!query || query.trim().length === 0) {
      return allKeywords.slice(0, limit);
    }

    const searchTerm = query.toLowerCase().trim();
    
    // Score keywords based on relevance
    const scoredKeywords = allKeywords.map(keyword => {
      const keywordLower = keyword.toLowerCase();
      let score = 0;

      // Exact match gets highest score
      if (keywordLower === searchTerm) {
        score = 1000;
      }
      // Starts with query gets high score
      else if (keywordLower.startsWith(searchTerm)) {
        score = 900;
      }
      // Contains query gets medium score
      else if (keywordLower.includes(searchTerm)) {
        score = 500;
      }
      // Word boundary match gets good score
      else if (keywordLower.includes(' ' + searchTerm) || keywordLower.includes(searchTerm + ' ')) {
        score = 700;
      }

      return { keyword, score };
    });

    // Filter and sort by score, then return top results.
    // De-duplicate case-insensitively FIRST, then slice: the corpus carries the
    // same keyword in several casings ("H&E Staining" and "H&E staining"), and
    // showing both reads as a bug. Slicing first would let near-duplicates eat
    // the limit and hide genuinely different suggestions. The best-scoring
    // casing wins, which is the one the user is most likely typing towards.
    const seen = new Set<string>();
    return scoredKeywords
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .filter(item => {
        const key = item.keyword.toLowerCase().trim();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, limit)
      .map(item => item.keyword);
  }

  /**
   * Clear cache and force refresh
   */
  async refreshCache(): Promise<string[]> {
    this.clearCache();
    return this.getKeywords();
  }

  /**
   * Preload keywords in the background (call this on app startup)
   */
  async preloadKeywords(): Promise<void> {
    try {
      await this.getKeywords();
      console.log('✅ Keywords preloaded successfully');
    } catch (error) {
      console.error('⚠️ Keywords preload failed:', error);
    }
  }

  /**
   * Get cache statistics
   */
  getCacheInfo(): { isCached: boolean; keywordCount: number; cacheAge: number; isExpired: boolean } {
    const cached = this.getCachedData();
    return {
      isCached: !!cached,
      keywordCount: this.keywords.length || (cached?.keywords.length ?? 0),
      cacheAge: cached ? Date.now() - cached.timestamp : 0,
      isExpired: !this.isCacheFresh()
    };
  }

  private async loadKeywordsFromCacheOrAPI(): Promise<string[]> {
    // Try to load from localStorage first
    const cached = this.getCachedData();
    if (cached && this.isCacheFresh(cached.timestamp)) {
      console.log('📦 Loading keywords from cache:', cached.keywords.length);
      return cached.keywords;
    }

    // Fetch from API
    console.log('🔄 Fetching keywords from API...');
    try {
      const { apiService } = await import('../services/apiService');
      const response = await apiService.getAllTechnologyKeywordsV2();
      
      if (response.success && response.data.keywords_base64) {
        // Decode base64 keywords
        const keywordsJson = atob(response.data.keywords_base64);
        const keywords = JSON.parse(keywordsJson) as string[];
        
        // Cache the results
        this.setCachedData({
          keywords,
          timestamp: Date.now(),
          totalCount: response.data.total_count
        });

        console.log('✅ Keywords loaded from API:', keywords.length);
        return keywords;
      } else {
        console.error('❌ Failed to load keywords:', response.error);
        return [];
      }
    } catch (error) {
      console.error('❌ Error loading keywords:', error);
      return [];
    }
  }

  private getCachedData(): KeywordsCacheData | null {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      if (cached) {
        return JSON.parse(cached) as KeywordsCacheData;
      }
    } catch (error: any) {
      // If quota exceeded or other storage error, clear and continue
      if (error.name === 'QuotaExceededError' || error.code === 22 || error.code === 1014) {
        console.warn('⚠️ localStorage quota error while reading cache. Clearing cache...');
        this.clearCache();
      } else {
        console.error('Error reading keywords cache:', error);
      }
    }
    return null;
  }

  private estimateCacheSize(data: KeywordsCacheData): number {
    // Rough estimate: each character is ~1 byte, plus JSON overhead
    return JSON.stringify(data).length;
  }

  private setCachedData(data: KeywordsCacheData): void {
    try {
      const dataString = JSON.stringify(data);
      const estimatedSize = dataString.length;
      
      // Warn if cache is getting large (> 4MB)
      if (estimatedSize > 4 * 1024 * 1024) {
        console.warn(`⚠️ Keywords cache is large (${(estimatedSize / 1024 / 1024).toFixed(2)}MB). Consider limiting keywords.`);
      }
      
      localStorage.setItem(this.cacheKey, dataString);
    } catch (error: any) {
      // Handle quota exceeded error
      if (error.name === 'QuotaExceededError' || error.code === 22 || error.code === 1014) {
        console.warn('⚠️ localStorage quota exceeded for keywords cache. Attempting to free space...');
        
        // Try to clear old cache and retry with a smaller dataset
        try {
          this.clearCache();
          
          // If still too large, try storing only a subset of keywords (top 10000 most common)
          // This should be well under 1MB
          const maxKeywords = 100000;
          if (data.keywords.length > maxKeywords) {
            console.warn(`⚠️ Keywords cache too large (${data.keywords.length} keywords), storing only top ${maxKeywords} keywords`);
            const limitedData: KeywordsCacheData = {
              keywords: data.keywords.slice(0, maxKeywords),
              timestamp: data.timestamp,
              totalCount: data.totalCount
            };
            localStorage.setItem(this.cacheKey, JSON.stringify(limitedData));
            console.log(`✅ Stored limited keywords cache (${maxKeywords} keywords)`);
          } else {
            // Retry with full data after clearing
            localStorage.setItem(this.cacheKey, JSON.stringify(data));
            console.log('✅ Stored keywords cache after clearing old data');
          }
        } catch (retryError) {
          console.warn('⚠️ Unable to store keywords cache even after clearing. Using in-memory cache only.');
          // Continue with in-memory cache - keywords are already loaded in this.keywords
        }
      } else {
        console.error('Error setting keywords cache:', error);
      }
    }
  }

  private isCacheFresh(timestamp?: number): boolean {
    const cached = this.getCachedData();
    const cacheTimestamp = timestamp || cached?.timestamp || 0;
    return Date.now() - cacheTimestamp < this.cacheExpiry;
  }

  private clearCache(): void {
    try {
      localStorage.removeItem(this.cacheKey);
      this.keywords = [];
    } catch (error) {
      console.error('Error clearing keywords cache:', error);
    }
  }
}

// Export singleton instance
export const keywordsCache = KeywordsCache.getInstance();
